/**
 * Utility functions for handling asset URLs with CDN support and WebP conversion
 */

let webpSupported = null;

const CLOUDFLARE_CDN_URL = 'https://pub-3cbdf0fe4d9c40f19241eb1a5a52005c.r2.dev/om-assets';

/**
 * Gets the base URL for assets, using CDN when available
 * @returns {string} The base URL for assets
 */
export const getAssetBaseUrl = () => {
  const cdnUrl = process.env.REACT_APP_CDN_URL || CLOUDFLARE_CDN_URL;
  return cdnUrl;
};

/**
 * Checks if the browser supports WebP format
 * @returns {boolean} True if the browser supports WebP
 */
const isWebPSupported = () => {
  if (webpSupported !== null) return webpSupported;

  if (typeof window !== 'undefined') {
    const canvas = document.createElement('canvas');
    if (canvas.getContext && canvas.getContext('2d')) {
      webpSupported = canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0;
    } else {
      webpSupported = false;
    }
  } else {
    webpSupported = false;
  }

  return webpSupported;
};

/**
 * Checks if the path has a convertible image extension
 * @param {string} path - The file path to check
 * @returns {boolean} True if the path has a convertible extension
 */
const hasConvertibleExtension = (path) => {
  const convertibleExts = ['.jpg', '.jpeg', '.png', '.gif'];
  return convertibleExts.some(ext => path.toLowerCase().endsWith(ext));
};

/**
 * Converts image path to WebP if supported
 * @param {string} path - The image path
 * @returns {string} Path with WebP extension if WebP is supported
 */
const convertToWebP = (path) => {
  if (!isWebPSupported() || !hasConvertibleExtension(path)) return path;

  return path.replace(/\.(jpe?g|png|gif)$/i, '.webp');
};

/**
 * Properly cleans asset paths by removing unnecessary prefixes
 * @param {string} path - The asset path to clean
 * @returns {string} The cleaned path
 */
const cleanAssetPath = (path) => {
  let cleanPath = path;

  // Handle "../assets/" pattern from book JSON files
  if (cleanPath.startsWith('../assets/')) {
    cleanPath = cleanPath.substring(3); // Remove "../" prefix
  }

  if (cleanPath.startsWith('public/')) {
    cleanPath = cleanPath.substring(7);
  }

  if (cleanPath.startsWith('assets/')) {
    cleanPath = cleanPath.substring(7);
  }

  if (cleanPath.startsWith('/assets/')) {
    cleanPath = cleanPath.substring(8);
  }

  if (cleanPath.startsWith('/')) {
    cleanPath = cleanPath.substring(1);
  }

  return cleanPath;
};

/**
 * Gets the public URL for an asset, optionally converting images to WebP
 * @param {string} path - The asset path relative to the public directory
 * @param {Object} options - Options for asset URL generation
 * @param {boolean} options.forceWebP - Force WebP conversion even if browser doesn't support it
 * @returns {string} The complete URL to the asset
 */
export const getAssetUrl = (path, options = {}) => {
  if (!path) return '';
  
  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  const cdnUrl = getAssetBaseUrl();
  if (cdnUrl) {
    let cleanPath = cleanAssetPath(path);

    if (options.forceWebP || isWebPSupported()) {
      cleanPath = convertToWebP(cleanPath);
    }

    const cleanCdnUrl = cdnUrl.endsWith('/') ? cdnUrl.slice(0, -1) : cdnUrl;
    const result = `${cleanCdnUrl}/${cleanPath}`;
    return result;
  }


  if (!path.startsWith('/')) {
    return `/${path}`;
  }

  return path;
}; 